import type { FC, ReactNode } from 'react'

import MockupFigure from '@/components/landing/mockup-figure'
import PhoneFrame from '@/components/landing/mockups/phone-frame'
import LayoutPickerScreen from '@/components/landing/mockups/layout-picker-screen'
import SectionIntro, { Eyebrow } from '@/components/landing/section-intro'

import {
    AudioScreen,
    FiltersScreen,
    FramesScreen,
    GhostControlsScreen,
    GhostOverlayScreen,
    PlaylistScreen,
    VideoWallScreen,
    ZoomScreen
} from '@/components/landing/mockups/tool-screens'

interface Tool {
    eyebrow: string
    title: string
    description: string
    figure: string
    screen: ReactNode
}

const TOOLS: Tool[] = [
    {
        eyebrow: 'Zoom per video',
        title: 'Get closer to the detail.',
        description: 'Adjust the framing of each shot separately to see exactly what you need.',
        figure: 'Illustrative view: a full shot with its focus area next to the zoomed view of that detail',
        screen: <ZoomScreen />
    },
    {
        eyebrow: 'Independent audio',
        title: 'Choose what you hear.',
        description: "Control each video's volume and mute the tracks you don't want in the mix.",
        figure: 'Illustrative view: four videos with a separate volume slider and mute switch for each one',
        screen: <AudioScreen />
    },
    {
        eyebrow: 'Filters',
        title: 'Change the mood.',
        description: 'Give each video a different look with the available filters. The original file is preserved.',
        figure: 'Illustrative view: the same shot shown with four different color filters',
        screen: <FiltersScreen />
    },
    {
        eyebrow: 'Frames & borders',
        title: 'Every view, your style.',
        description: 'Combine borders, colors and frames to give your composition its own identity.',
        figure: 'Illustrative view: three videos, each with its own glowing colored frame',
        screen: <FramesScreen />
    },
    {
        eyebrow: 'Video playlists',
        title: 'Everything ready to play.',
        description: 'Organize your clips in playlists, reorder them and choose which ones to play.',
        figure: 'Illustrative view: the playlist manager with a list of clips, their order and a preview',
        screen: <PlaylistScreen />
    },
    {
        eyebrow: 'Video wall simulator',
        title: 'One image. Many views.',
        description: 'Explore how a shot looks when it is split across a composition of screens with the simulator.',
        figure: 'Illustrative view: one shot spread across a wall of six screens',
        screen: <VideoWallScreen />
    }
]

const cardTitle = 'font-heading font-extrabold leading-[1.08] tracking-[-0.03em] text-foreground'

const FeaturesSection: FC = (): ReactNode => {
    return (
        <section id='features' aria-labelledby='features-title' className='border-t border-border bg-background py-20 lg:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionIntro
                    id='features-title'
                    eyebrow="Tools you'll notice"
                    title='More control over every video.'
                    intro='Pick your files, arrange the screen and adjust each view separately.'
                />

                <p className='mt-8 text-xs text-muted-foreground'>
                    Illustrative views. Check the options available for your device and plan in the app.
                </p>

                <div className='mt-5 space-y-5'>
                    <article className='reveal grid overflow-hidden rounded-2xl border border-border bg-card lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]'>
                        <div className='p-7 sm:p-9 lg:p-10'>
                            <Eyebrow>
                                Grids & Manga
                            </Eyebrow>

                            <h3 className={`mt-4 text-[clamp(1.75rem,3vw,2.25rem)] ${cardTitle}`}>
                                Up to 12 views.
                                <br />
                                You choose the layout.
                            </h3>

                            <p className='mt-4 max-w-md text-[0.97rem] leading-relaxed text-muted-foreground'>
                                Combine videos and photos in classic grids or Manga layouts. Use the free options and expand with PRO.
                            </p>

                            <p className='mt-5 max-w-md text-xs leading-relaxed text-muted-foreground'>
                                Simultaneous playback depends on the files, their resolution and your device's capabilities. Some layouts require PRO.
                            </p>
                        </div>

                        <MockupFigure
                            label='Illustrative view: the screen layout picker with classic and Manga layouts'
                            title='Choose your screen'
                            className='border-t border-border lg:border-l lg:border-t-0'
                            stageClassName='flex items-center'
                        >
                            <PhoneFrame>
                                <LayoutPickerScreen />
                            </PhoneFrame>
                        </MockupFigure>
                    </article>

                    <article className='reveal overflow-hidden rounded-2xl border border-border bg-card'>
                        <div className='p-7 sm:p-9 lg:p-10'>
                            <Eyebrow>
                                Ghost Mode
                            </Eyebrow>

                            <h3 className={`mt-4 text-[clamp(1.75rem,3vw,2.25rem)] ${cardTitle}`}>
                                Two takes.
                                <br />
                                The same movement.
                            </h3>

                            <p className='mt-4 max-w-lg text-[0.97rem] leading-relaxed text-muted-foreground'>
                                Overlay two videos, pick the segment and adjust the blend, speed and alignment. Save your comparison to continue later.
                            </p>
                        </div>

                        <div className='grid divide-y divide-border border-t border-border md:grid-cols-2 md:divide-x md:divide-y-0'>
                            <MockupFigure
                                label='Illustrative view: Ghost Mode controls with trims for two videos, blend and speed'
                                title='Adjust the segment and blend'
                                caption='Adjust the segment and blend'
                            >
                                <PhoneFrame>
                                    <GhostControlsScreen />
                                </PhoneFrame>
                            </MockupFigure>

                            <MockupFigure
                                label='Illustrative view: two takes of the same movement overlaid, with the controls hidden'
                                title='Compare without controls in the way'
                                caption='Compare without controls in the way'
                            >
                                <PhoneFrame>
                                    <GhostOverlayScreen />
                                </PhoneFrame>
                            </MockupFigure>
                        </div>
                    </article>

                    <div className='grid gap-5 md:grid-cols-2'>
                        {TOOLS.map((tool) => (
                            <article key={tool.eyebrow} className='reveal flex flex-col overflow-hidden rounded-2xl border border-border bg-card'>
                                <MockupFigure label={tool.figure} title={tool.title}>
                                    <PhoneFrame>
                                        {tool.screen}
                                    </PhoneFrame>
                                </MockupFigure>

                                <div className='flex-1 border-t border-border p-7 sm:p-8'>
                                    <Eyebrow>
                                        {tool.eyebrow}
                                    </Eyebrow>

                                    <h3 className={`mt-3 text-2xl ${cardTitle}`}>
                                        {tool.title}
                                    </h3>

                                    <p className='mt-3 max-w-md text-[0.95rem] leading-relaxed text-muted-foreground'>
                                        {tool.description}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default FeaturesSection
